import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useBranding } from "../context/BrandingContext";
import { getCompany } from "../store/actions";
import { hasTheme, loadTheme } from "./registry";

// Carga el paquete de componentes de la org (si tiene uno) y lo expone a los
// Slots. Mientras un tema real está cargando, `loading` es true: las pantallas
// muestran un loader en vez de pintar la UI por defecto y luego saltar.
const ThemeContext = createContext({ name: "default", theme: null, loading: false });

// Con sesión, la org (y por lo tanto el tema) sale de redux `company`, que
// carga AppShell. Si se entra directo a una ruta del tema (/noticias) o se
// recarga ahí, AppShell todavía no montó: nadie pidió la org y el router cae
// a la UI por defecto. El id de la org del usuario está en localStorage.
const sessionCompanyId = () => {
  try {
    if (localStorage.getItem("isAuthenticated") !== "true") return null;
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user?.company?.id || null;
  } catch {
    return null;
  }
};

export const ThemeProvider = ({ children }) => {
  const { branding, org } = useBranding();
  const name = branding?.theme && hasTheme(branding.theme) ? branding.theme : "default";
  const [state, setState] = useState({ name: "default", theme: null, loading: false });
  const dispatch = useDispatch();
  const company = useSelector((s) => s.company);
  const [sessionChecked, setSessionChecked] = useState(false);
  const pendingCompanyId = company && company._id ? null : sessionChecked ? null : sessionCompanyId();

  useEffect(() => {
    if (!pendingCompanyId) return undefined;
    let cancelled = false;
    Promise.resolve(dispatch(getCompany(pendingCompanyId)))
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSessionChecked(true);
      });
    return () => {
      cancelled = true;
    };
  }, [pendingCompanyId, dispatch]);

  useEffect(() => {
    let cancelled = false;
    if (name === "default") {
      setState({ name: "default", theme: null, loading: false });
      return undefined;
    }
    setState((current) => (current.name === name && current.theme ? current : { name, theme: null, loading: true }));
    loadTheme(name)
      .then((theme) => {
        if (!cancelled) setState({ name, theme, loading: false });
      })
      .catch((error) => {
        console.error(`No se pudo cargar el tema "${name}":`, error);
        if (!cancelled) setState({ name: "default", theme: null, loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [name]);

  // Mientras no sepamos la org de la sesión tampoco sabemos el tema: loading.
  const value = useMemo(
    () => ({ ...state, loading: state.loading || Boolean(pendingCompanyId), sessionPending: Boolean(pendingCompanyId), org }),
    [state, org, pendingCompanyId]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext);

export const ThemeLoader = () => (
  <div className="min-h-screen bg-slate-900 flex items-center justify-center">
    <div className="w-5 h-5 border-2 border-slate-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Punto de reemplazo. `<Slot name="Home" fallback={<Worlds />} />` renderiza el
 * componente `Home` del tema de la org si existe; si no, el fallback.
 * Las props extra llegan al componente del tema.
 */
export const Slot = ({ name, fallback = null, children, ...props }) => {
  const { theme, loading } = useTheme();
  if (loading) return <ThemeLoader />;
  const Component = theme?.[name];
  if (!Component) return fallback;
  return <Component {...props}>{children}</Component>;
};

export default ThemeContext;
